import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import {
  FOCUSABLE_SELECTOR,
  useFieldError,
  useFieldRegistry,
} from "shared/hooks/useFieldRegistry";

/**
 * Registers a field's DOM anchor with the field registry and re-renders only
 * when that field's error changes. Children receive error/touched props via
 * a render function so shared Input components stay unchanged.
 */
const FormField = ({
  path,
  label,
  required = false,
  disabled = false,
  children,
  className = "",
}) => {
  const store = useFieldRegistry();
  const wrapperRef = useRef(null);
  const error = useFieldError(path);

  useEffect(() => {
    if (!path || !wrapperRef.current) {
      return undefined;
    }

    const focusField = () => {
      if (disabled) {
        return;
      }
      const focusable = wrapperRef.current?.querySelector(FOCUSABLE_SELECTOR);
      focusable?.focus?.({ preventScroll: true });
    };

    store.register(path, {
      element: wrapperRef.current,
      label,
      required,
      disabled,
      focusField,
    });

    return () => {
      store.unregister(path);
    };
  }, [disabled, label, path, required, store]);

  useEffect(() => {
    if (!path) {
      return;
    }
    store.update(path, { label, required, disabled });
  }, [disabled, label, path, required, store]);

  const fieldProps = {
    name: path,
    id: path,
    label,
    required,
    disabled,
    errors: error || undefined,
    touched: Boolean(error),
    // A client dossier field must never surface the advisor's own saved data:
    // browsers read a NIR as a card security code and a birth country as a
    // personal address, and propose them in front of the client.
    autoComplete: "off",
  };

  return (
    <div
      ref={wrapperRef}
      className={["form-field", className].filter(Boolean).join(" ")}
      data-field-path={path}
    >
      {typeof children === "function" ? children(fieldProps) : children}
    </div>
  );
};

FormField.propTypes = {
  path: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  className: PropTypes.string,
};

FormField.defaultProps = {
  label: undefined,
  required: false,
  disabled: false,
  className: "",
};

export default FormField;
